import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Clock, ArrowRight, Trash2, Check, BarChart2, Map as MapIcon, Search, Pencil, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { chatApi } from '../../api/chatApi';
import { workspaceApi } from '../../api/workspaceApi';

const ChatHistoryModal = ({ isOpen, onClose, onSelectChat, workspaceId, onSelectArtefact }) => {
  const [activeTab, setActiveTab] = useState('history'); // 'history', 'chart', 'map'
  const [chats, setChats] = useState([]);
  const [artefacts, setArtefacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingArtefacts, setIsLoadingArtefacts] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [renamingArtefactId, setRenamingArtefactId] = useState(null);
  const [deletingArtefactId, setDeletingArtefactId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const loadChats = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await chatApi.getChats(workspaceId);
      setChats(data);
    } catch (err) {
      console.error('Failed to load chats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const loadArtefacts = useCallback(async () => {
    setIsLoadingArtefacts(true);
    try {
      const data = await workspaceApi.getWorkspaceArtefacts(workspaceId);
      setArtefacts(data);
    } catch (err) {
      console.error('Failed to load artefacts:', err);
    } finally {
      setIsLoadingArtefacts(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'history') {
        loadChats();
      } else {
        loadArtefacts();
      }
    }
  }, [isOpen, activeTab, loadChats, loadArtefacts]);

  const filteredItems = activeTab === 'history'
    ? chats.filter(c => (c.title || 'Untitled Chat').toLowerCase().includes(searchQuery.toLowerCase()))
    : artefacts.filter(art => {
      const matchesSearch = art.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = art.type === activeTab;
      return matchesSearch && matchesType;
    });

  const handleDelete = async (e, chatId) => {
    e.stopPropagation();
    setDeletingChatId(chatId);
  };

  const confirmDelete = async (e, chatId) => {
    e.stopPropagation();
    try {
      await chatApi.deleteChat(chatId);
      setChats(prev => prev.filter(c => c.id !== chatId));
      setDeletingChatId(null);
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  const handleSelect = async (chat) => {
    try {
      const messages = await chatApi.getMessages(chat.id);
      onSelectChat(chat.id, messages, chat.title);
      onClose();
    } catch (err) {
      console.error('Failed to load chat messages:', err);
    }
  };

  const handleDeleteArtefact = async (e, id) => {
    e.stopPropagation();
    try {
      await workspaceApi.deleteArtefact(id);
      setArtefacts(prev => prev.filter(art => art.id !== id));
      setDeletingArtefactId(null);
    } catch (err) {
      console.error('Failed to delete artefact:', err);
    }
  };

  const handleRenameArtefact = async (e, id) => {
    e.stopPropagation();
    try {
      await workspaceApi.updateArtefact(id, { title: editTitle });
      setArtefacts(prev => prev.map(art => art.id === id ? { ...art, title: editTitle } : art));
      setRenamingArtefactId(null);
    } catch (err) {
      console.error('Failed to rename artefact:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-6xl h-[85vh] rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
        <div className="p-10 pb-6 shrink-0">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className={cn(
                "p-3 rounded-[20px]",
                activeTab === 'history' ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600" :
                  activeTab === 'chart' ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" :
                    "bg-purple-100 dark:bg-purple-900/30 text-purple-600"
              )}>
                {activeTab === 'history' ? <Clock size={24} /> : activeTab === 'chart' ? <BarChart2 size={24} /> : <MapIcon size={24} />}
              </div>
              <div>
                <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none mb-1">
                  Workspace Gallery
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                  Manage your recent activity and saved assets
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 dark:border-gray-800">
            {/* Tabs */}
            <div className="flex items-center gap-8 h-12">
              {[
                { id: 'history', label: 'History', icon: MessageSquare },
                { id: 'chart', label: 'Charts', icon: BarChart2 },
                { id: 'map', label: 'Layers', icon: MapIcon }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchQuery('');
                  }}
                  className={cn(
                    "relative flex items-center gap-2 h-full text-[10px] font-black uppercase tracking-[0.2em] transition-all px-1",
                    activeTab === tab.id
                      ? "text-gray-900 dark:text-white"
                      : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  )}
                >
                  <tab.icon size={14} />
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 dark:bg-white rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full max-w-xs group mb-4 md:mb-0">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-gray-900 dark:group-focus-within:text-white transition-colors" size={14} />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'history' ? 'sessions' : activeTab === 'chart' ? 'charts' : 'layers'}...`}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-transparent focus:border-gray-200 dark:focus:border-gray-800 rounded-xl text-[10px] font-bold text-gray-900 dark:text-white focus:outline-none transition-all placeholder:text-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-10 pb-10 custom-scrollbar">
          {activeTab === 'history' ? (
            <div className="max-w-4xl mx-auto space-y-2">
              {isLoading ? (
                <div className="py-20 flex flex-col items-center justify-center gap-4">
                  <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Retrieving sessions...</span>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="py-20 text-center">
                  <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">No recent chats found</p>
                </div>
              ) : (
                filteredItems.map((chat) => (
                  <div
                    key={chat.id}
                    className={cn(
                      "w-full flex items-center gap-4 p-3 rounded-[24px] border transition-all text-left group relative overflow-hidden",
                      deletingChatId === chat.id
                        ? "border-red-500/50 bg-red-50/10"
                        : "border-gray-100 dark:border-gray-800"
                    )}
                  >
                    <div className={cn(
                      "p-2.5 rounded-xl transition-colors",
                      deletingChatId === chat.id ? "bg-red-100 text-red-600" : "bg-gray-50 dark:bg-gray-900 text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                    )}>
                      <Clock size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4
                        onClick={() => !deletingChatId && handleSelect(chat)}
                        className={cn(
                          "text-sm font-black uppercase tracking-tight truncate transition-colors",
                          deletingChatId === chat.id ? "text-red-600" : "text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 cursor-pointer"
                        )}
                      >
                        {deletingChatId === chat.id ? 'Delete session?' : (chat.title || 'Untitled Chat')}
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          {new Date(chat.created_at).toLocaleDateString()} • {new Date(chat.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <span className="w-1 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                          {chat.message_count || 0} messages
                        </p>
                      </div>
                    </div>

                    {deletingChatId === chat.id ? (
                      <div className="flex items-center gap-2 animate-in slide-in-from-right-2 duration-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingChatId(null);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={(e) => confirmDelete(e, chat.id)}
                          className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all shadow-sm shadow-red-500/20"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(e, chat.id);
                          }}
                          className="p-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          onClick={() => handleSelect(chat)}
                          className="p-2 text-gray-300 hover:text-gray-900 dark:hover:text-white group-hover:translate-x-1 transition-all"
                        >
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div>
              {isLoadingArtefacts ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(i => (
                    <div key={i} className="aspect-[4/5] rounded-[24px] bg-gray-50 dark:bg-gray-900 animate-pulse" />
                  ))}
                </div>
              ) : filteredItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredItems.map((art) => (
                    <div
                      key={art.id}
                      className="group flex flex-col bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-[24px] overflow-hidden transition-all duration-300"
                    >
                      <div className="aspect-[16/10] bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center relative border-b border-gray-50 dark:border-gray-800">
                        <div className="relative z-0 flex flex-col items-center gap-2">
                          <span className={cn(
                            "p-2.5 rounded-2xl shadow-sm transition-transform duration-300 group-hover:scale-110",
                            art.type === 'chart' ? "bg-amber-100 text-amber-600" : "bg-purple-100 text-purple-600"
                          )}>
                            {art.type === 'chart' ? <BarChart2 size={20} /> : <MapIcon size={20} />}
                          </span>
                        </div>

                        <div className="absolute inset-0 bg-white/90 dark:bg-gray-950/90 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-3 z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSelectArtefact) {
                                onSelectArtefact(art);
                                onClose();
                              }
                            }}
                            className={cn(
                              "w-full py-2.5 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all outline-none",
                              art.type === 'chart' ? "bg-amber-600 shadow-amber-600/20" : "bg-purple-600 shadow-purple-600/20"
                            )}
                          >
                            {art.type === 'map' ? (
                              <><Plus size={14} strokeWidth={3} /> Add</>
                            ) : (
                              <><ArrowRight size={14} strokeWidth={3} /> Open</>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="p-4 flex flex-col flex-1 justify-between">
                        <div className="min-h-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className={cn(
                              "text-[8px] font-black uppercase tracking-[0.2em]",
                              art.type === 'chart' ? "text-amber-600" : "text-purple-600"
                            )}>
                              {art.type === 'chart'
                                ? `${art.spec?.mark?.type || art.spec?.mark || 'chart'}`.toUpperCase()
                                : `layer`.toUpperCase()
                              }
                            </span>

                            {/* Rename/Delete only for maps */}
                            {art.type === 'map' && (
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenamingArtefactId(art.id);
                                    setEditTitle(art.title);
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  title="Rename"
                                >
                                  <Pencil size={11} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeletingArtefactId(art.id);
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            )}
                          </div>

                          {renamingArtefactId === art.id ? (
                            <div className="mb-2 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <input
                                type="text"
                                autoFocus
                                value={editTitle}
                                onBlur={() => setRenamingArtefactId(null)}
                                onKeyDown={(e) => e.key === 'Enter' && handleRenameArtefact(e, art.id)}
                                onChange={e => setEditTitle(e.target.value)}
                                className="w-full text-xs font-black uppercase tracking-tight py-1.5 px-2 rounded-lg bg-gray-50 dark:bg-gray-800 border-2 border-purple-500/50 outline-none"
                              />
                            </div>
                          ) : deletingArtefactId === art.id ? (
                            <div className="mb-2 flex items-center justify-between p-1.5 bg-red-50 dark:bg-red-900/20 rounded-lg" onClick={e => e.stopPropagation()}>
                              <span className="text-[8px] font-black text-red-600 uppercase tracking-widest pl-1">Del?</span>
                              <div className="flex items-center gap-1">
                                <button onClick={(e) => setDeletingArtefactId(null)} className="p-1 text-gray-400"><X size={11} /></button>
                                <button onClick={(e) => handleDeleteArtefact(e, art.id)} className="p-1 bg-red-500 text-white rounded-lg"><Check size={11} /></button>
                              </div>
                            </div>
                          ) : (
                            <h4 className="text-[12px] font-black text-gray-900 dark:text-white uppercase tracking-tight line-clamp-2 min-h-[1.5rem] leading-[1.3] group-hover:text-gray-950 dark:group-hover:text-white transition-colors">
                              {art.title}
                            </h4>
                          )}
                        </div>

                        <div className="mt-2.5 pt-2 border-t border-gray-50 dark:border-gray-800">
                          {art.type === 'chart' && (
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.05em] mb-1 truncate">
                              {art.chat_title}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <Clock size={10} />
                            <span className="text-[9px] font-black uppercase tracking-widest">{art.timestamp}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-20 flex flex-col items-center justify-center text-center">
                  <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-[32px] text-gray-200 mb-6">
                    {activeTab === 'chart' ? <BarChart2 size={48} /> : <MapIcon size={48} />}
                  </div>
                  <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight mb-2">
                    {searchQuery ? "No matching results" : `No saved ${activeTab}s`}
                  </h4>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest max-w-[200px]">
                    {searchQuery ? "Try adjusting your search terms" : `New ${activeTab}s will appear here as you create them`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryModal;
